import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { fadeInUp, staggerContainer } from "@/motion/variants";
import { TradingPartnersTrustBar } from "@/components/TrustBar";
import Navigation from "@/components/Navigation";
import SEO from "@/components/SEO";
import Footer from "@/components/Footer";
import { TradingTrustStrip } from '@/components/TradingTrustStrip';
import { 
  Ship, 
  Shield, 
  FileText, 
  CreditCard, 
  CheckCircle, 
  Building2, 
  FileCheck, 
  Banknote,
  PackageOpen,
  Clock,
  MapPin,
  Download,
  Mail,
  Calendar,
  TrendingDown,
  TrendingUp,
  Settings,
  Container,
  FlaskConical,
  Truck,
  FileWarning
} from "lucide-react";

/**
 * PalmProducts Page
 * Trading page for LC-backed palm oil & olein exports from Malaysia to World
 * Business logic: Converts FOB Malaysia to CFR/CIF World using transferable LCs,
 * standardized SGS inspection scope, with both containerized flexitank and bulk execution
 * Containerized: 20' GP flexitank (~22 MT nominal)
 * Bulk: Minimum 3,000 MT, maximum varies by port specifics
 * Leverages deep supplier network relationships for global reach
 */
const PalmProducts = () => {
  return (
    <div className="min-h-screen pt-6">
      <SEO
        title="LC-Backed Palm Oil & Olein to World | CFR/CIF Trading | Padmora"
        description="Convert FOB Malaysia palm products into CFR/CIF World with transferable LCs, standardized SGS scope, and both containerized flexitank and bulk execution. RBD Palm Olein, Palm Super Olein, RBDPO, and more. Leveraging deep supplier network relationships."
        image="/og-trading.jpg"
        canonical="https://padmora.com/trading/palm-products"
        keywords={[
          "palm oil trading",
          "palm olein Ghana",
          "palm olein Bangladesh",
          "palm olein Egypt",
          "CFR Ghana palm oil",
          "CIF Ghana palm oil",
          "CFR Bangladesh palm oil",
          "CIF Bangladesh palm oil",
          "CFR Egypt palm oil",
          "CIF Egypt palm oil",
          "RBD palm olein",
          "Malaysia palm exports",
          "LC-backed palm trading",
          "SGS inspection palm oil",
          "flexitank palm oil",
          "bulk palm oil shipping",
          "Ghana palm oil import",
          "Bangladesh palm oil import",
          "Egypt palm oil import",
          "Port Klang palm oil",
          "transferable LC palm"
        ]}
        aiOptimization={{
          aiKeywords: [
            "automated palm oil logistics",
            "standardized SGS inspection scope",
            "containerized edible oil trading",
            "bulk palm oil shipping",
            "programmatic CFR/CIF palm products",
            "LC documentation automation"
          ],
          entities: [
            "Palm Oil",
            "Palm Olein",
            "Ghana",
            "Bangladesh",
            "Egypt",
            "Port Klang",
            "SGS Inspection",
            "Flexitank",
            "Bulk Shipping"
          ],
          topics: [
            "Edible Oil Trading",
            "Palm Products Export",
            "Ghana Imports",
            "Bangladesh Imports",
            "Egypt Imports",
            "Documentary Credits",
            "Containerized Shipping",
            "Bulk Commodity Shipping"
          ]
        }}
        structuredData={{
          "@context": "https://schema.org",
          "@type": "Service",
          "name": "Palm Oil & Olein Trading Service",
          "description": "LC-backed palm oil and olein trading from Malaysia to World with standardized documentation, offering both containerized flexitank and bulk execution, leveraging deep supplier network relationships.",
          "provider": {
            "@type": "Organization",
            "name": "Padmora"
          },
          "serviceType": "Commodity Trading",
          "areaServed": [
            {
              "@type": "Country",
              "name": "Ghana"
            },
            {
              "@type": "Country",
              "name": "Bangladesh"
            },
            {
              "@type": "Country",
              "name": "Egypt"
            }
          ],
          "additionalProperty": [
            {
              "@type": "PropertyValue",
              "name": "Trade Type",
              "value": "CFR/CIF Containerized & Bulk"
            },
            {
              "@type": "PropertyValue",
              "name": "Payment Method",
              "value": "Transferable Letter of Credit"
            },
            {
              "@type": "PropertyValue",
              "name": "Inspection",
              "value": "SGS Certificate"
            },
            {
              "@type": "PropertyValue",
              "name": "Origin",
              "value": "Port Klang, Malaysia"
            }
          ]
        }}
        breadcrumbs={[
          { name: "Home", url: "https://padmora.com/" },
          { name: "Trading", url: "https://padmora.com/trading" },
          { name: "Palm Products → World", url: "https://padmora.com/trading/palm-products" }
        ]}
      />
      <Navigation />

      {/* Hero Section */}
      <section className="min-h-screen flex flex-col justify-center">
        <div className="container mx-auto px-4 py-20">
          <motion.div
            variants={staggerContainer}
            initial="initial"
            animate="animate"
            className="max-w-4xl mx-auto text-center"
          >
            <motion.h1
              variants={fadeInUp}
              className="text-4xl md:text-6xl font-bold text-slate-900 mb-6"
            >
              LC-backed Palm Products to the World, done right
            </motion.h1>
            <motion.p
              variants={fadeInUp}
              className="text-xl text-slate-600 mb-8"
            >
              We convert FOB Malaysia into CFR/CIF World, using transferable/confirmed LCs, standardized SGS scope, and both containerized flexitank and bulk execution. Our deep supplier network relationships ensure reliable global reach. Outcomes over optics.
            </motion.p>
            <motion.div variants={fadeInUp} className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                size="lg"
                className="px-8 py-4 text-lg"
                asChild
              >
                <a
                  href="mailto:michel@padmora.com?subject=CFR%2FCIF%20Quote%20Request%20-%20Palm%20Products"
                  rel="noopener noreferrer"
                >
                  <Mail className="mr-2 h-5 w-5" />
                  Get CFR/CIF Quote
                </a>
              </Button>
              <Button
                size="lg"
                variant="outline"
                className="px-8 py-4 text-lg"
                asChild
              >
                <a
                  href="mailto:michel@padmora.com"
                  className="text-slate-700 hover:text-slate-900"
                >
                  <Mail className="mr-2 h-5 w-5" />
                  Send enquiry
                </a>
              </Button>
            </motion.div>
          </motion.div>

                    <TradingTrustStrip />

          {/* Partners */}
          <div className="mt-16">
            <TradingPartnersTrustBar />
          </div>
        </div>
      </section>

      {/* Problem → Solution */}
      <section className="py-20 bg-slate-50">
        <div className="container mx-auto px-4">
          <motion.div
            variants={staggerContainer}
            initial="initial"
            animate="animate"
            className="grid md:grid-cols-2 gap-12 max-w-6xl mx-auto"
          >
            {/* Problem */}
            <motion.div
              variants={fadeInUp}
              className="bg-white border border-red-100 p-8 rounded-lg shadow-sm"
            >
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                  <TrendingDown className="h-6 w-6 text-red-600" />
                </div>
                <h2 className="text-2xl font-bold text-slate-900">
                  Freight whiplash and doc friction kill margins
                </h2>
              </div>
              <p className="text-slate-600 leading-relaxed">
                Rates swing 1–2k per box and edible-oil docs get nit-picked. We standardize LC wording, inspection, and vendor payments so your program clears even on ugly weeks.
              </p>
            </motion.div>

            {/* Solution */}
            <motion.div
              variants={fadeInUp}
              className="bg-white border border-green-100 p-8 rounded-lg shadow-sm"
            >
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                  <Settings className="h-6 w-6 text-green-600" />
                </div>
                <h2 className="text-2xl font-bold text-slate-900">
                  Programmatic CFR/CIF from Port Klang
                </h2>
              </div>
              <p className="text-slate-600 leading-relaxed">
                Identical templates, uniform SGS wording, and bankable flows. Your LC issues to us, we transfer, ship in flexis or bulk, present clean, and you get the product.
              </p>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* What we ship */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <motion.div
            variants={staggerContainer}
            initial="initial"
            animate="animate"
            className="max-w-6xl mx-auto"
          >
            <motion.h2
              variants={fadeInUp}
              className="text-3xl font-bold text-center mb-4"
            >
              What we ship
            </motion.h2>
            <motion.p
              variants={fadeInUp}
              className="text-slate-600 text-center mb-12 max-w-3xl mx-auto"
            >
              Containerized: 20' GP flexitank (~22 MT nominal) | Bulk: Min 3,000 MT (max varies by port), ex-Port Klang to CFR/CIF World
            </motion.p>
            
            {/* Shipping Methods */}
            <motion.div
              variants={staggerContainer}
              className="grid md:grid-cols-2 gap-6 mb-12"
            >
              <motion.div
                variants={fadeInUp}
                className="bg-card border border-border p-6 rounded-lg"
              >
                <div className="flex items-center gap-3 mb-4">
                  <Container className="h-6 w-6 text-blue-600" />
                  <h3 className="text-xl font-semibold">Containerized (Flexitank)</h3>
                </div>
                <ul className="space-y-2 text-slate-600 text-sm">
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 text-green-600 mt-0.5 flex-shrink-0" />
                    <span>20' GP flexitank ~22 MT nominal</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 text-green-600 mt-0.5 flex-shrink-0" />
                    <span>Food-grade flexitank with thermal liner</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 text-green-600 mt-0.5 flex-shrink-0" />
                    <span>Ideal for smaller volumes and specific destinations</span>
                  </li>
                </ul>
              </motion.div>

              <motion.div
                variants={fadeInUp}
                className="bg-card border border-border p-6 rounded-lg"
              >
                <div className="flex items-center gap-3 mb-4">
                  <Ship className="h-6 w-6 text-blue-600" />
                  <h3 className="text-xl font-semibold">Bulk Shipping</h3>
                </div>
                <ul className="space-y-2 text-slate-600 text-sm">
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 text-green-600 mt-0.5 flex-shrink-0" />
                    <span>Minimum lift: 3,000 MT</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 text-green-600 mt-0.5 flex-shrink-0" />
                    <span>Maximum varies by port specifics</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 text-green-600 mt-0.5 flex-shrink-0" />
                    <span>Cost-effective for large volume programs</span>
                  </li>
                </ul>
              </motion.div>
            </motion.div>

            {/* Core Products */}
            <motion.div
              variants={staggerContainer}
              className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8"
            >
              <motion.div
                variants={fadeInUp}
                className="bg-card border border-border p-6 rounded-lg hover:shadow-lg transition-shadow"
              >
                <div className="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center mb-4">
                  <PackageOpen className="h-6 w-6 text-amber-600" />
                </div>
                <h3 className="text-lg font-semibold mb-2">RBD Palm Olein</h3>
                <p className="text-sm text-slate-600 mb-3">CP8 / CP10</p>
                <div className="text-xs text-slate-500 space-y-1">
                  <p>• Available in flexitank & bulk</p>
                  <p>• ~22 MT per 20' GP (containerized)</p>
                  <p>• SGS load inspection + COA</p>
                </div>
              </motion.div>

              <motion.div
                variants={fadeInUp}
                className="bg-card border border-border p-6 rounded-lg hover:shadow-lg transition-shadow"
              >
                <div className="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center mb-4">
                  <PackageOpen className="h-6 w-6 text-amber-600" />
                </div>
                <h3 className="text-lg font-semibold mb-2">Palm Super Olein</h3>
                <p className="text-sm text-slate-600 mb-3">High IV variant</p>
                <div className="text-xs text-slate-500 space-y-1">
                  <p>• Premium clarity specs</p>
                  <p>• Thermal liner flexi (containerized)</p>
                  <p>• Consistent batch quality</p>
                </div>
              </motion.div>

              <motion.div
                variants={fadeInUp}
                className="bg-card border border-border p-6 rounded-lg hover:shadow-lg transition-shadow"
              >
                <div className="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center mb-4">
                  <PackageOpen className="h-6 w-6 text-amber-600" />
                </div>
                <h3 className="text-lg font-semibold mb-2">RBD Palm Oil</h3>
                <p className="text-sm text-slate-600 mb-3">RBDPO</p>
                <div className="text-xs text-slate-500 space-y-1">
                  <p>• Standard refined grade</p>
                  <p>• Industrial/food use</p>
                  <p>• Available in both formats</p>
                </div>
              </motion.div>

              <motion.div
                variants={fadeInUp}
                className="bg-card border border-border p-6 rounded-lg hover:shadow-lg transition-shadow"
              >
                <div className="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center mb-4">
                  <PackageOpen className="h-6 w-6 text-amber-600" />
                </div>
                <h3 className="text-lg font-semibold mb-2">Palm Stearin</h3>
                <p className="text-sm text-slate-600 mb-3">High melting fraction</p>
                <div className="text-xs text-slate-500 space-y-1">
                  <p>• Shortening applications</p>
                  <p>• Industrial oleochemicals</p>
                  <p>• Bundled availability</p>
                </div>
              </motion.div>
            </motion.div>

            {/* Additional Products */}
            <motion.div
              variants={staggerContainer}
              className="grid md:grid-cols-3 gap-6"
            >
              <motion.div
                variants={fadeInUp}
                className="bg-slate-50 border border-slate-200 p-6 rounded-lg"
              >
                <h3 className="text-lg font-semibold mb-2">Palm Kernel Oil</h3>
                <p className="text-sm text-slate-600">PKO - Specialty applications, cosmetic/food grade</p>
              </motion.div>

              <motion.div
                variants={fadeInUp}
                className="bg-slate-50 border border-slate-200 p-6 rounded-lg"
              >
                <h3 className="text-lg font-semibold mb-2">PFAD</h3>
                <p className="text-sm text-slate-600">Palm Fatty Acid Distillate - Industrial feedstock</p>
              </motion.div>

              <motion.div
                variants={fadeInUp}
                className="bg-slate-50 border border-slate-200 p-6 rounded-lg"
              >
                <h3 className="text-lg font-semibold mb-2">Upsell Bundles</h3>
                <p className="text-sm text-slate-600">Shortening, Margarine base, Soap Noodles (80/20, 90/10), Crude/Refined Glycerine</p>
              </motion.div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* How it works */}
      <section className="py-20 bg-slate-50">
        <div className="container mx-auto px-4">
          <motion.div
            variants={staggerContainer}
            initial="initial"
            animate="animate"
            className="max-w-6xl mx-auto"
          >
            <motion.h2
              variants={fadeInUp}
              className="text-3xl font-bold text-center mb-12"
            >
              How it works
            </motion.h2>
            <motion.div
              variants={staggerContainer}
              className="grid md:grid-cols-2 lg:grid-cols-4 gap-8"
            >
              {/* Step 1 */}
              <motion.div
                variants={fadeInUp}
                className="bg-white border border-border p-6 rounded-lg shadow-sm"
              >
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                    <span className="text-lg font-bold text-blue-600">1</span>
                  </div>
                  <h3 className="text-lg font-semibold">LC in, space held</h3>
                </div>
                <p className="text-slate-600 text-sm leading-relaxed">
                  Transferable LC issued to Padmora. We hold 2 allocations ex-Port Klang for your window.
                </p>
              </motion.div>

              {/* Step 2 */}
              <motion.div
                variants={fadeInUp}
                className="bg-white border border-border p-6 rounded-lg shadow-sm"
              >
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                    <span className="text-lg font-bold text-blue-600">2</span>
                  </div>
                  <h3 className="text-lg font-semibold">SGS scope + execution</h3>
                </div>
                <p className="text-slate-600 text-sm leading-relaxed">
                  Standard load inspection + COA. Containerized: food-grade flexis with thermal liner. Bulk: vessel-specific protocols. Uniform SGS wording for consistency.
                </p>
              </motion.div>

              {/* Step 3 */}
              <motion.div
                variants={fadeInUp}
                className="bg-white border border-border p-6 rounded-lg shadow-sm"
              >
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                    <span className="text-lg font-bold text-blue-600">3</span>
                  </div>
                  <h3 className="text-lg font-semibold">Ship & present</h3>
                </div>
                <p className="text-slate-600 text-sm leading-relaxed">
                  Clean BL on board, present uniform docs, and execute direct-pay to vendors where applicable.
                </p>
              </motion.div>

              {/* Step 4 */}
              <motion.div
                variants={fadeInUp}
                className="bg-white border border-border p-6 rounded-lg shadow-sm"
              >
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                    <span className="text-lg font-bold text-blue-600">4</span>
                  </div>
                  <h3 className="text-lg font-semibold">Delivery & close</h3>
                </div>
                <p className="text-slate-600 text-sm leading-relaxed">
                  Buyer clears under applicable import certification per consignment/importer; we reconcile proceeds and roll the next clip.
                </p>
              </motion.div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* LC & Docs */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <motion.div
            variants={staggerContainer}
            initial="initial"
            animate="animate"
            className="max-w-6xl mx-auto"
          >
            <motion.h2
              variants={fadeInUp}
              className="text-3xl font-bold text-center mb-12"
            >
              LC & docs we accept
            </motion.h2>
            
            <motion.div
              variants={staggerContainer}
              className="grid md:grid-cols-2 gap-8"
            >
              {/* Letter of Credit Card */}
              <motion.div variants={fadeInUp} className="bg-card border border-border p-8 rounded-lg">
                <div className="flex items-center gap-3 mb-4">
                  <CreditCard className="h-6 w-6 text-blue-600" />
                  <h3 className="text-xl font-semibold">Letter of Credit</h3>
                </div>
                <div className="space-y-2 text-slate-600">
                  <p className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 text-green-600 mt-1 flex-shrink-0" />
                    <span><span className="font-medium">UCP600</span> compliant</span>
                  </p>
                  <p className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 text-green-600 mt-1 flex-shrink-0" />
                    <span><span className="font-medium">Transferable</span>, partials allowed</span>
                  </p>
                  <p className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 text-green-600 mt-1 flex-shrink-0" />
                    <span><span className="font-medium">Transshipment</span> permitted</span>
                  </p>
                  <p className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 text-green-600 mt-1 flex-shrink-0" />
                    <span><span className="font-medium">±10%</span> qty/amount tolerance</span>
                  </p>
                  <p className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 text-green-600 mt-1 flex-shrink-0" />
                    <span><span className="font-medium">21-day</span> presentation period</span>
                  </p>
                </div>
              </motion.div>

              {/* Required Documents Card */}
              <motion.div variants={fadeInUp} className="bg-card border border-border p-8 rounded-lg">
                <div className="flex items-center gap-3 mb-4">
                  <FileText className="h-6 w-6 text-blue-600" />
                  <h3 className="text-xl font-semibold">Required Documents</h3>
                </div>
                <div className="space-y-2 text-slate-600">
                  <p className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 text-green-600 mt-1 flex-shrink-0" />
                    <span><span className="font-medium">3/3 OBL</span> to order</span>
                  </p>
                  <p className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 text-green-600 mt-1 flex-shrink-0" />
                    <span><span className="font-medium">Commercial Invoice</span></span>
                  </p>
                  <p className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 text-green-600 mt-1 flex-shrink-0" />
                    <span><span className="font-medium">Packing List</span></span>
                  </p>
                  <p className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 text-green-600 mt-1 flex-shrink-0" />
                    <span><span className="font-medium">SGS</span> load inspection + COA</span>
                  </p>
                  <p className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 text-green-600 mt-1 flex-shrink-0" />
                    <span><span className="font-medium">Import Certification</span> (destination-specific)</span>
                  </p>
                  <p className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 text-green-600 mt-1 flex-shrink-0" />
                    <span><span className="font-medium">Insurance cert</span> (if CIF/add-on)</span>
                  </p>
                </div>
              </motion.div>
            </motion.div>

            {/* Assignment of proceeds - Full width below */}
            <motion.div variants={fadeInUp} className="mt-6 bg-slate-50 border border-slate-200 p-4 rounded-lg">
              <p className="text-sm text-slate-600 text-center">
                <span className="font-medium">Assignment of proceeds</span> permitted to pay nominated vendors on presentation (optional line to ease bank ops)
              </p>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Compliance & Inspection */}
      <section className="py-20 bg-slate-50">
        <div className="container mx-auto px-4">
          <motion.div
            variants={staggerContainer}
            initial="initial"
            animate="animate"
            className="max-w-4xl mx-auto"
          >
            <motion.h2
              variants={fadeInUp}
              className="text-3xl font-bold text-center mb-4"
            >
              One SGS scope, zero surprises
            </motion.h2>
            <motion.p
              variants={fadeInUp}
              className="text-slate-600 text-center mb-12"
            >
              We fix the inspection wording once and run it every lot. Typical parameters for edible oils, with retain samples, CQ/COQ at load. Applicable to both containerized flexitank and bulk shipments.
            </motion.p>
            
            <motion.div
              variants={fadeInUp}
              className="bg-white border border-border p-8 rounded-lg"
            >
              <div className="flex items-center gap-3 mb-6">
                <FlaskConical className="h-6 w-6 text-blue-600" />
                <h3 className="text-xl font-semibold">Standard Inspection Scope</h3>
              </div>
              <div className="grid md:grid-cols-2 gap-6 text-slate-600">
                <div>
                  <h4 className="font-semibold text-slate-900 mb-3">Load Parameters</h4>
                  <ul className="space-y-2 text-sm">
                    <li className="flex items-start gap-2">
                      <CheckCircle className="h-4 w-4 text-green-600 mt-0.5 flex-shrink-0" />
                      <span>Quantity verification (weight/volume)</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="h-4 w-4 text-green-600 mt-0.5 flex-shrink-0" />
                      <span>Containerized: Flexitank integrity & food-grade cert</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="h-4 w-4 text-green-600 mt-0.5 flex-shrink-0" />
                      <span>Bulk: Vessel tank cleanliness & certification</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="h-4 w-4 text-green-600 mt-0.5 flex-shrink-0" />
                      <span>Container seal & cleanliness (containerized)</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="h-4 w-4 text-green-600 mt-0.5 flex-shrink-0" />
                      <span>Retain samples (sealed, labeled)</span>
                    </li>
                  </ul>
                </div>
                <div>
                  <h4 className="font-semibold text-slate-900 mb-3">Quality Tests (COA)</h4>
                  <ul className="space-y-2 text-sm">
                    <li className="flex items-start gap-2">
                      <CheckCircle className="h-4 w-4 text-green-600 mt-0.5 flex-shrink-0" />
                      <span>FFA, moisture, impurities</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="h-4 w-4 text-green-600 mt-0.5 flex-shrink-0" />
                      <span>Iodine value, slip melting point</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="h-4 w-4 text-green-600 mt-0.5 flex-shrink-0" />
                      <span>Color (Lovibond), clarity</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="h-4 w-4 text-green-600 mt-0.5 flex-shrink-0" />
                      <span>Per agreed product specification</span>
                    </li>
                  </ul>
                </div>
              </div>
              <div className="mt-6 p-4 bg-blue-50 border border-blue-100 rounded-lg">
                <p className="text-sm text-slate-700">
                  <FileWarning className="inline h-4 w-4 text-blue-600 mr-2" />
                  <span className="font-medium">See our specimen scope</span> for complete structure and parameter limits
                </p>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Ops & Lead Times */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <motion.div
            variants={staggerContainer}
            initial="initial"
            animate="animate"
            className="max-w-6xl mx-auto"
          >
            <motion.h2
              variants={fadeInUp}
              className="text-3xl font-bold text-center mb-12"
            >
              Ops & lead times
            </motion.h2>
            <motion.div
              variants={staggerContainer}
              className="grid md:grid-cols-2 lg:grid-cols-4 gap-6"
            >
              <motion.div
                variants={fadeInUp}
                className="bg-card border border-border p-6 rounded-lg"
              >
                <MapPin className="h-8 w-8 text-blue-600 mb-4" />
                <h3 className="font-semibold mb-2">Origin</h3>
                <p className="text-slate-600 text-sm">Port Klang, Malaysia</p>
              </motion.div>

              <motion.div
                variants={fadeInUp}
                className="bg-card border border-border p-6 rounded-lg"
              >
                <Container className="h-8 w-8 text-blue-600 mb-4" />
                <h3 className="font-semibold mb-2">Packaging</h3>
                <p className="text-slate-600 text-sm">Containerized: 20' GP flexitank ~22 MT nominal | Bulk: Min 3,000 MT (max port-specific)</p>
              </motion.div>

              <motion.div
                variants={fadeInUp}
                className="bg-card border border-border p-6 rounded-lg"
              >
                <Clock className="h-8 w-8 text-blue-600 mb-4" />
                <h3 className="font-semibold mb-2">Lead Time</h3>
                <p className="text-slate-600 text-sm">2–3 weeks to booking, ETD per allocation; 10–16 days transit</p>
              </motion.div>

              <motion.div
                variants={fadeInUp}
                className="bg-card border border-border p-6 rounded-lg"
              >
                <Truck className="h-8 w-8 text-blue-600 mb-4" />
                <h3 className="font-semibold mb-2">Payment Flows</h3>
                <p className="text-slate-600 text-sm">LC at sight/usance; vendor D+10/D+15 via assignment</p>
              </motion.div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Insurance */}
      <section className="py-20 bg-slate-50">
        <div className="container mx-auto px-4">
          <motion.div
            variants={staggerContainer}
            initial="initial"
            animate="animate"
            className="max-w-4xl mx-auto"
          >
            <motion.h2
              variants={fadeInUp}
              className="text-3xl font-bold text-center mb-12"
            >
              Insurance: CFR & CIF Options
            </motion.h2>
            <motion.div
              variants={staggerContainer}
              className="grid md:grid-cols-2 gap-8"
            >
              <motion.div
                variants={fadeInUp}
                className="bg-white border border-border p-6 rounded-lg"
              >
                <div className="flex items-center gap-3 mb-4">
                  <Shield className="h-6 w-6 text-blue-600" />
                  <h3 className="text-xl font-semibold">CFR (Cost & Freight)</h3>
                </div>
                <ul className="space-y-2 text-slate-600 text-sm">
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 text-green-600 mt-0.5 flex-shrink-0" />
                    <span>Optional ICC(A) + SRCC/war add-on</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 text-green-600 mt-0.5 flex-shrink-0" />
                    <span>Certificates issued per BL</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 text-green-600 mt-0.5 flex-shrink-0" />
                    <span>Claims support provided</span>
                  </li>
                </ul>
              </motion.div>

              <motion.div
                variants={fadeInUp}
                className="bg-white border border-green-200 p-6 rounded-lg"
              >
                <div className="flex items-center gap-3 mb-4">
                  <Shield className="h-6 w-6 text-green-600" />
                  <h3 className="text-xl font-semibold">CIF (Cost, Insurance & Freight)</h3>
                </div>
                <ul className="space-y-2 text-slate-600 text-sm">
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 text-green-600 mt-0.5 flex-shrink-0" />
                    <span>110% sum insured (invoice value)</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 text-green-600 mt-0.5 flex-shrink-0" />
                    <span>Loss payee: issuing/confirming bank</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 text-green-600 mt-0.5 flex-shrink-0" />
                    <span>Full claims assistance & documentation</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 text-green-600 mt-0.5 flex-shrink-0" />
                    <span>Available for both containerized & bulk</span>
                  </li>
                </ul>
              </motion.div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Who it's for */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <motion.div
            variants={staggerContainer}
            initial="initial"
            animate="animate"
            className="max-w-4xl mx-auto"
          >
            <motion.h2
              variants={fadeInUp}
              className="text-3xl font-bold text-center mb-12"
            >
              Who it's for
            </motion.h2>
            <motion.div
              variants={staggerContainer}
              className="grid md:grid-cols-3 gap-6"
            >
              <motion.div
                variants={fadeInUp}
                className="bg-card/50 border border-border p-6 rounded-lg text-center"
              >
                <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mb-4 mx-auto">
                  <Building2 className="h-8 w-8 text-blue-600" />
                </div>
                <h3 className="font-semibold mb-2">Global Packers & Refineries</h3>
                <p className="text-sm text-slate-600">Refineries, fillers wanting containerized or bulk continuity without babysitting origins, leveraging our deep supplier network</p>
              </motion.div>

              <motion.div
                variants={fadeInUp}
                className="bg-card/50 border border-border p-6 rounded-lg text-center"
              >
                <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mb-4 mx-auto">
                  <TrendingUp className="h-8 w-8 text-blue-600" />
                </div>
                <h3 className="font-semibold mb-2">Brokers & Distributors</h3>
                <p className="text-sm text-slate-600">Needing bankable LC-to-execution coverage with clean documentation</p>
              </motion.div>

              <motion.div
                variants={fadeInUp}
                className="bg-card/50 border border-border p-6 rounded-lg text-center"
              >
                <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mb-4 mx-auto">
                  <Ship className="h-8 w-8 text-blue-600" />
                </div>
                <h3 className="font-semibold mb-2">Import Programs</h3>
                <p className="text-sm text-slate-600">Running regular programs who value predictable docs, freight, and delivery timing</p>
              </motion.div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Why us */}
      <section className="py-20 bg-slate-50">
        <div className="container mx-auto px-4">
          <motion.div
            variants={staggerContainer}
            initial="initial"
            animate="animate"
            className="max-w-4xl mx-auto text-center"
          >
            <motion.h2
              variants={fadeInUp}
              className="text-3xl font-bold mb-6"
            >
              Why us (outcomes)
            </motion.h2>
            <motion.p
              variants={fadeInUp}
              className="text-xl text-slate-600 mb-12"
            >
              Uniform text. Vendor direct-pay. Hard space first. Clean presentations. You get product on time instead of excuses.
            </motion.p>
            <motion.div
              variants={staggerContainer}
              className="grid md:grid-cols-5 gap-4"
            >
              <motion.div variants={fadeInUp} className="bg-white border border-border p-4 rounded-lg">
                <FileText className="h-6 w-6 text-blue-600 mx-auto mb-2" />
                <p className="text-sm font-medium">Uniform text</p>
              </motion.div>
              <motion.div variants={fadeInUp} className="bg-white border border-border p-4 rounded-lg">
                <CreditCard className="h-6 w-6 text-blue-600 mx-auto mb-2" />
                <p className="text-sm font-medium">Vendor direct-pay</p>
              </motion.div>
              <motion.div variants={fadeInUp} className="bg-white border border-border p-4 rounded-lg">
                <Ship className="h-6 w-6 text-blue-600 mx-auto mb-2" />
                <p className="text-sm font-medium">Hard space first</p>
              </motion.div>
              <motion.div variants={fadeInUp} className="bg-white border border-border p-4 rounded-lg">
                <CheckCircle className="h-6 w-6 text-blue-600 mx-auto mb-2" />
                <p className="text-sm font-medium">Clean presentations</p>
              </motion.div>
              <motion.div variants={fadeInUp} className="bg-white border border-border p-4 rounded-lg">
                <Clock className="h-6 w-6 text-blue-600 mx-auto mb-2" />
                <p className="text-sm font-medium">On-time delivery</p>
              </motion.div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20">
        <div className="container mx-auto px-4 text-center">
          <motion.div
            variants={staggerContainer}
            initial="initial"
            animate="animate"
            className="max-w-2xl mx-auto"
          >
            <motion.h2
              variants={fadeInUp}
              className="text-3xl font-bold text-slate-900 mb-6"
            >
              Ready to move palm products worldwide?
            </motion.h2>
            <motion.p
              variants={fadeInUp}
              className="text-slate-600 mb-8"
            >
              Get a CFR/CIF quote, review our LC draft template, or schedule a 15-minute call to discuss your containerized or bulk requirements.
            </motion.p>
            <motion.div variants={fadeInUp} className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                size="lg"
                variant="default"
                className="px-8 py-4 text-lg bg-primary hover:bg-primary/90 text-white"
                asChild
              >
                <a
                  href="mailto:michel@padmora.com?subject=CFR%2FCIF%20Quote%20Request%20-%20Palm%20Products"
                  rel="noopener noreferrer"
                >
                  <Mail className="mr-2 h-5 w-5" />
                  Get CFR/CIF Quote
                </a>
              </Button>
              <Button
                size="lg"
                variant="outline"
                className="px-8 py-4 text-lg"
                asChild
              >
                <a
                  href="/public/docs/Padmora_SPECIMEN_LC_MT700_Template_header.pdf"
                >
                  <Download className="mr-2 h-5 w-5" />
                  Download LC Draft
                </a>
              </Button>
              <Button
                size="lg"
                variant="outline"
                className="px-8 py-4 text-lg"
                asChild
              >
                <a
                  href="mailto:michel@padmora.com"
                >
                  <Mail className="mr-2 h-5 w-5" />
                  Send enquiry
                </a>
              </Button>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default PalmProducts;

